import { type ColumnDef, type SortingState } from '@tanstack/react-table'

import { type FunctionComponent, useState } from 'react'

import { type Application } from '../../types/application'

import useDeleteApplicationMutation from 'src/hooks/services/graph/mutations/useDeleteApplicationMutation'

import { PiTrash } from 'react-icons/pi'

import Table from '../Common/Table/Table'
import ConfirmationModal from '../Modals/ConfirmationModal'

interface Props {
  applications: Application[]
  onEditApplication?: (application: Application) => void
}

const ApplicationTable: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { applications, onEditApplication } = props

  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean
    application: Application | null
  }>({ isOpen: false, application: null })
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { mutateAsync: deleteApplication } = useDeleteApplicationMutation()

  const columns: ColumnDef<Application>[] = [
    {
      header: 'Name',
      accessorKey: 'displayName',
      cell: ({ getValue }) => <span className="font-bold">{getValue() as string}</span>
    },
    {
      header: 'App ID',
      accessorKey: 'appId',
      cell: ({ getValue }) => <span className="font-mono text-sm text-th-text-secondary">{getValue() as string}</span>
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-center">
          <button
            className="text-th-error transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteModalState({ isOpen: true, application: row.original })
            }}
            title="Delete application"
            disabled={deletingId === row.original.appId}
          >
            {deletingId === row.original.appId ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-th-text-secondary border-t-transparent" />
            ) : (
              <PiTrash className="h-4 w-4" />
            )}
          </button>
        </div>
      )
    }
  ]

  const defaultSorting: SortingState = [
    {
      id: 'displayName',
      desc: false
    }
  ]

  const handleRowClick = (application: Application) => {
    if (deletingId === application.appId) return
    if (deleteModalState.isOpen && deleteModalState.application?.appId === application.appId) return
    onEditApplication?.(application)
  }

  const closeDeleteModal = () => {
    setDeleteModalState({ isOpen: false, application: null })
  }

  const confirmDelete = async () => {
    if (deleteModalState.application) {
      const appId = deleteModalState.application.appId
      closeDeleteModal()
      setDeletingId(appId)
      try {
        await deleteApplication(appId)
      } finally {
        setDeletingId(null)
      }
    }
  }

  const DeleteConfirmationModal = (
    <ConfirmationModal
      isOpen={deleteModalState.isOpen}
      onClose={closeDeleteModal}
      onConfirm={confirmDelete}
      title="Delete Application"
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
    >
      <div className="space-y-4">
        <p>
          Are you sure you want to delete the application <strong>"{deleteModalState.application?.displayName}"</strong>?
        </p>

        <div className="bg-th-warning/10 rounded-lg border border-th-warning p-3 text-sm">
          <p className="mb-2 font-bold text-th-warning">⚠️ Warning</p>
          <p className="text-th-warning">
            Deleting this application will break any external integrations that rely on it for authentication (for example, integrations using this app's client
            credentials or tokens). Those systems will no longer be able to authenticate until reconfigured with a different application.
          </p>
        </div>

        <div className="rounded-lg bg-th-content-secondary p-3 text-sm">
          <p className="mb-1 font-semibold">App ID:</p>
          <p className="font-mono break-all text-th-text-secondary">{deleteModalState.application?.appId}</p>
        </div>
      </div>
    </ConfirmationModal>
  )

  return (
    <>
      <Table columns={columns} data={applications} defaultSortBy={defaultSorting} onRowClick={handleRowClick} />
      {DeleteConfirmationModal}
    </>
  )
}

export default ApplicationTable
